/* eslint-disable import/prefer-default-export */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback, useEffect, useMemo, useState } from "react";

// eslint-disable-next-line import/extensions
import { fetchRoiCalculationData } from "../apicalls/farmFunctions";
// eslint-disable-next-line import/extensions
import { Currency, RoiCalculationData, RoiScenario, RoiStageInfo } from "../../../graphql/types";
// eslint-disable-next-line import/extensions
import { buildRoiFormData, parseAwsJson, RoiFormData } from "../helpers/roiStageData";

export interface ParsedRoiStage {
  stageId: string | null;
  stageName: string | null;
  stageType: string | null;
  stageData: Record<string, unknown> | null;
}

export interface ParsedRoiScenario {
  reference: string | null;
  name: string | null;
  stages: ParsedRoiStage[];
  impactSummary: Record<string, unknown> | null;
  category: string | null;
  defaultStageName: string | null;
  defaultStageType: string | null;
}

export interface RoiCalculationDataParsed {
  defaultCurrency: Currency | null;
  intervention: ParsedRoiScenario;
  baseline: ParsedRoiScenario;
}

interface UseRoiCalculationDataResult {
  data: RoiCalculationDataParsed | null;
  formData: RoiFormData;
  loading: boolean;
  failed: boolean;
  refetch: () => void;
}

const parseStageData = (raw: unknown): Record<string, unknown> | null =>
  parseAwsJson<Record<string, unknown>>(raw);

const parseScenario = (
  scenario?: RoiScenario | null
): ParsedRoiScenario => ({
  reference: scenario?.reference ?? null,
  name: scenario?.name ?? null,
  impactSummary: parseAwsJson<Record<string, unknown>>(scenario?.impactSummary),
  category: scenario?.category ?? null,
  defaultStageName: scenario?.defaultStageName ?? null,
  defaultStageType: scenario?.defaultStageType ?? null,
  stages: (scenario?.stages ?? [])
    .filter((stage): stage is RoiStageInfo => Boolean(stage))
    .map((stage) => ({
      stageId: stage.stageId ?? null,
      stageName: stage.stageName ?? null,
      stageType: stage.stageType ?? null,
      stageData: parseStageData(stage.stageData),
    })),
});

const parseRoiCalculationData = (
  raw: RoiCalculationData
): RoiCalculationDataParsed => ({
  defaultCurrency: raw.defaultCurrency ?? null,
  intervention: parseScenario(raw.intervention),
  baseline: parseScenario(raw.baseline),
});

export const useRoiCalculationData = (
  farmId?: string,
  interventionReference?: string,
  selectedCurrency?: string | null
): UseRoiCalculationDataResult => {
  const [data, setData] = useState<RoiCalculationDataParsed | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!farmId || !interventionReference) return undefined;

    let active = true;
    setLoading(true);
    setFailed(false);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      try {
        const roiData: RoiCalculationData | null =
          await fetchRoiCalculationData(farmId, interventionReference);

        if (!active) return;
        setData(roiData ? parseRoiCalculationData(roiData) : null);
      } catch (err) {
        if (!active) return;
        const graphqlError = err as
          | { errors?: Array<{ message?: string }> }
          | undefined;
        // eslint-disable-next-line no-console
        console.error(
          "getRoiCalculationData failed:",
          graphqlError?.errors?.[0]?.message ?? err
        );
        setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [farmId, interventionReference, reloadKey]);

  const formData = useMemo(
    () => buildRoiFormData(data, selectedCurrency),
    [data, selectedCurrency]
  );

  return { data, formData, loading, failed, refetch };
};
